export enum BlockType {
  timed = 'timed',
  tutor = 'tutor',
}

export enum BlockTimeSetting {
  default = 'default',
  quarter = 'quarter',
  half = 'half',
  double = 'double',
}

export const MAX_FLASHCARD_COUNT = 9999;
export const MAX_QBANK_COUNT = 320;

export interface StudySession {
  id: string;
  date: string;
  flashcardCount: number;
  completedFlashcardCount: number;
  vignetteCount: number;
  videoCount: number;
  videoWatchedCount: number;
  blockCount: number;
  isComplete: boolean;
  isStarted: boolean;
  activeBlockId: string;
  vignetteCorrectCount: number;
  vignetteIncorrectCount: number;
  vignetteOmittedCount: number;
  isReview: boolean;
  isSnoozed: boolean;
  isOffDay?: boolean;
  shouldShowOffDayUI?: boolean;
  isScheduledOffDay?: boolean;
  studiedAnywayOnOffDay?: boolean;
  nextStudyDate?: string | null;
  hasSeenPriorities: boolean;
  isUsingFlashcards: boolean;
  isUsingQbank: boolean;
  isUsingVideos: boolean;
  isFirstSession: boolean;
  userId: string;
  examId: string;
}

export interface BlockAnswers {
  [key: string]: {
    answer: string;
    correctAnswer: string;
    submitted: boolean;
    vignetteContentId: string;
    secondAnswer?: string;
    secondSubmitted?: boolean;
  };
}
// Generic key-value mapping type for block data
export type BlockKeyValueMap<T> = {
  [key: string]: T;
};

export interface ContentFilterRef {
  id: string;
  name?: string;
}

export interface Block {
  id: string;
  /** Optional user-given block name; overrides the derived list title when set. */
  name?: string | null;
  type: BlockType;
  answers: BlockAnswers;
  // Combined similar key-value interfaces into single type
  flags: BlockKeyValueMap<boolean>;
  notes: BlockKeyValueMap<string>;
  answerCrossouts: BlockKeyValueMap<boolean[]>;
  questionHighlights: BlockKeyValueMap<string>;
  date: Date;
  duration: number;
  timeLimitSeconds?: number | null;
  timeRemainingSeconds?: number | null;
  timeSpentSeconds?: number;
  isStarted: boolean;
  isSubmitted: boolean;
  correctCount: number;
  incorrectCount: number;
  answerCount?: number;
  omittedCount: number;
  vignetteCount: number;
  timeSetting: string;
  ord?: number;
  selfAssessmentAttemptId?: string | null;
  selfAssessmentBlockNumber?: number | null;
  selfAssessmentStartedAt?: string | Date | null;
  selfAssessmentSubmittedAt?: string | Date | null;
  selfAssessmentAccess?: {
    isSelfAssessment: boolean;
    isReviewable: boolean;
    isLocked: boolean;
    isActive: boolean;
    isExpired: boolean;
    attemptId?: string;
    timingMode?: 'strict' | 'flexible';
    assessmentSlug?: string;
    blockNumber?: number | null;
    // Forward-only practice-test blocks only (NCLEX-RN, spec area 4, K7):
    // the 0-based index of the furthest item reached. Absent elsewhere.
    navigation?: {
      forwardOnly: boolean;
      highestVisitedIndex: number;
    };
  } | null;
  isRecalculated?: boolean;
  isReviewAvailable?: boolean;
  isVariantRetryBlock?: boolean;
  /** NCLEX Format tour block (ORA-1127): show each item's format label. */
  isFormatTour?: boolean;
  sourceBlockIds?: string[] | null;
  sourceBlocks?: { id: string; isSubmitted: boolean }[];
  filterSetId?: string | null;
  filterSet?: {
    filterType: 'custom' | 'session' | 'topic';
    filters: {
      attribute?: string;
      statusMode?: string;
      scopeType?: string;
      studySessionId?: string;
      lectureIds?: string[];
      lectures?: Array<string | ContentFilterRef>;
      viewAllPage?: {
        version: 1;
        requestedCount: number;
        limit: number;
        cursor: string | null;
        nextCursor: string | null;
      };
    };
    exams: Array<string | ContentFilterRef>;
    topics: Array<string | ContentFilterRef>;
  };
}

export enum PanelType {
  explanation = 'explanation',
  library = 'library',
  report = 'report',
  flashcards = 'flashcards',
  chat = 'chat',
  videos = 'videos',
  document = 'document',
  textbook = 'textbook', // mcgraw-hill (ORA-1196)
}
